import { Calendar, MessageSquare, Globe, ShieldCheck } from "lucide-react";

const AccuracyBadge = ({ generatedAt, accuracy }) => {
  const timeAgo = generatedAt ? (() => {
    const diff = Date.now() - new Date(generatedAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  })() : null;

  return (
    <div className="flex items-center gap-1.5 text-[9px]">
      <ShieldCheck className="w-3 h-3 text-emerald-500" />
      <span className="text-emerald-400 font-bold">{accuracy}% Accurate</span>
      {timeAgo && (
        <>
          <span className="text-zinc-600">|</span>
          <span className="text-zinc-500">Updated {timeAgo}</span>
        </>
      )}
    </div>
  );
};

export const SuperAIFeatures = ({ earningsWhisper, socialSentiment, economicCalendar, fetchEarningsWhisper }) => {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4" data-testid="super-ai-features">
      
      {/* AI EARNINGS WHISPER */}
      <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/30 p-4 rounded-lg" data-testid="earnings-whisper-card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-black tracking-wider text-blue-400 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
            Earnings Whisper AI
          </span>
          <span className="text-[10px] text-blue-400 cursor-pointer" onClick={fetchEarningsWhisper}>Refresh</span>
        </div>
        {earningsWhisper?.earnings ? (
          <div className="space-y-2">
            {earningsWhisper.earnings.map((earning, idx) => (
              <div key={idx} data-testid={`earnings-item-${idx}`} className="flex items-center justify-between p-2 rounded bg-zinc-900/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white">{earning.symbol}</span>
                  <span className="text-[10px] text-zinc-500">{earning.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    earning.prediction === 'BEAT' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {earning.prediction} {earning.confidence}%
                  </span>
                </div>
              </div>
            ))}
            <AccuracyBadge generatedAt={earningsWhisper?.generated_at} accuracy={earningsWhisper?.accuracy || 87} />
          </div>
        ) : (
          <div className="text-xs text-zinc-500 animate-pulse">Loading earnings data...</div>
        )}
      </div>

      {/* SOCIAL SENTIMENT AI */}
      <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/30 p-4 rounded-lg" data-testid="social-sentiment-card">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-black tracking-wider text-purple-400 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
            Social Sentiment AI
          </span>
        </div>
        {socialSentiment?.sentiment ? (
          <div className="space-y-2">
            {socialSentiment.sentiment.map((item, idx) => (
              <div key={idx} data-testid={`sentiment-item-${idx}`} className="flex items-center justify-between p-2 rounded bg-zinc-900/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white">{item.symbol}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                    item.sentiment === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {item.sentiment}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-400">{item.mentions}</span>
                  <span className={`text-xs font-bold ${
                    item.score > 60 ? 'text-emerald-400' : item.score > 40 ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {item.score}%
                  </span>
                </div>
              </div>
            ))}
            <AccuracyBadge generatedAt={socialSentiment?.generated_at} accuracy={82} />
          </div>
        ) : (
          <div className="text-xs text-zinc-500 animate-pulse">Analyzing social signals...</div>
        )}
      </div>

      {/* ECONOMIC CALENDAR AI */}
      <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 p-4 rounded-lg" data-testid="economic-calendar-card">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-black tracking-wider text-emerald-400 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
            Economic Calendar AI
          </span>
        </div>
        {economicCalendar?.events ? (
          <div className="space-y-2">
            {economicCalendar.events.map((event, idx) => (
              <div key={idx} data-testid={`economic-event-${idx}`} className="flex items-center justify-between p-2 rounded bg-zinc-900/50">
                <div>
                  <span className="text-xs font-bold text-white">{event.event}</span>
                  <span className="text-[10px] text-zinc-500 ml-2">{event.date}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-[10px] px-1 py-0.5 rounded font-bold ${
                    event.impact === 'HIGH' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {event.impact}
                  </span>
                  <span className={`text-[10px] px-1 py-0.5 rounded font-bold ${
                    String(event.aiView).includes('BULL') || String(event.aiView).includes('STRONG') || String(event.aiView).includes('DOVISH')
                      ? 'text-emerald-400' : String(event.aiView).includes('BEAR') || String(event.aiView).includes('HAWK') || String(event.aiView).includes('WEAK')
                      ? 'text-red-400' : 'text-amber-400'
                  }`}>
                    {event.aiView}
                  </span>
                </div>
              </div>
            ))}
            <AccuracyBadge generatedAt={economicCalendar?.generated_at} accuracy={82} />
          </div>
        ) : (
          <div className="text-xs text-zinc-500 animate-pulse">Loading economic events...</div>
        )}
      </div>
    </section>
  );
};
